import { Button, Card, Col, Flex, Form, Input, Row, Select } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Zipcode from '../../../lib/Zipcode';
import { notifyError, notifySuccess } from '../../../store/utils';
import { API } from '../../../api';

const CustomerUpdate = () => {
  const { customerNo } = useParams();
  const [form] = Form.useForm();
  const [loading, updateLoading] = useState(false);
  const [branchs, updateBranchs] = useState<any[]>([]);

  const fetchBranchs = useCallback(async (query?: string) => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/branch/list1?page=1&size=20&query=${query || ''}`
      );
      updateBranchs(result?.data || []);
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const fetchFormData = useCallback(async () => {
    try {
      const [branchData, customerData] = await Promise.all([
        await API.get({ apiVersion: 'api' })(`/branch/list?page=1&size=20`),
        await API.get({ apiVersion: 'api' })(`/customer/${customerNo}`),
      ]);
      const branch = branchData?.result?.data || [];
      const customer = customerData?.result?.data || {};
      const branchs = [customer?.branch || [], ...branch];
      updateBranchs(branchs);

      const streets: any[] = [];
      if (customer.street) {
        streets.push(customer?.street?.province?.no);
        streets.push(customer?.street?.district?.no);
        streets.push(customer?.street?.no);
      }

      form.setFieldsValue({
        name: customer?.name,
        cd: customer?.cd,
        type: customer?.type?.code,
        branchNo: customer?.branch?.no,
        prefix: customer?.prefix,
        paymentType: customer?.paymentType.code,
        phone1: customer?.phone1,
        phone2: customer?.phone2,
        w3w: customer?.w3w,
        send: customer?.send?.code,
        status: customer?.status?.code,
        addressDetail: customer?.addressDetail,
        streetNo: streets,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [customerNo]);

  const onFinish = useCallback(
    async (values: any) => {
      try {
        updateLoading(true);
        const streetNos = values.streetNo;
        const payload = {
          name: values?.name,
          cd: values?.cd,
          prefix: values?.prefix,
          type: values?.type,
          branchNo: values?.branchNo,
          paymentType: values?.paymentType,
          phone1: values?.phone1,
          phone2: values?.phone2,
          w3w: values?.w3w,
          send: values?.send,
          status: values?.status,
          addressDetail: values?.addressDetail,
          streetNo: streetNos.at(-1),
        };
        if (customerNo) {
          await API.put({ apiVersion: 'api' })(`/customer/update/${customerNo}`, payload);
        } else {
          await API.post({ apiVersion: 'api' })(`/customer/create`, payload);
        }
        notifySuccess('Амжилтай хадгаллаа');
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        updateLoading(false);
      }
    },
    [customerNo]
  );

  useEffect(() => {
    if (customerNo) {
      fetchFormData();
    } else {
      fetchBranchs();
    }
  }, [customerNo]);

  return (
    <Card title={`Харилцагчид ${customerNo ? 'засах' : 'нэмэх'}`}>
      <Row>
        <Col span={12} offset={6}>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError={true}
            initialValues={{
              send: 'NO',
              status: 'ACTIVE',
              type: 'DEFAULT',
            }}
          >
            <Form.Item
              name="branchNo"
              label="Салбар"
              rules={[{ required: true, message: 'Салбар сонгоно уу' }]}
            >
              <Select
                showSearch
                allowClear
                filterOption={false}
                onSearch={(value: string) => fetchBranchs(value)}
                placeholder="Салбар сонгоно уу"
                options={branchs.map((branch: any) => {
                  return {
                    value: branch.no,
                    label: branch.name,
                  };
                })}
              />
            </Form.Item>
            <Form.Item
              name="name"
              label="Харилцагчын нэр"
              rules={[{ required: true, message: 'Харилцагчын нэр оруулна уу' }]}
            >
              <Input placeholder="Харилцагчын нэр оруулна уу" />
            </Form.Item>
            <Form.Item name="prefix" label="Баркодын prefix">
              <Input placeholder="Баркодын prefix оруулна уу" />
            </Form.Item>
            <Form.Item
              name="cd"
              label="Код"
              rules={[{ required: true, message: 'Код оруулна уу' }]}
            >
              <Input disabled={!!customerNo} placeholder="Код оруулна уу" />
            </Form.Item>
            <Form.Item
              name="type"
              label="Төрөл"
              rules={[{ required: true, message: 'Төрөл сонгоно уу' }]}
            >
              <Select>
                <Select.Option value="CN">Хятад харилцагч</Select.Option>
                <Select.Option value="DEFAULT">Дотоод</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="paymentType"
              label="Төлбөрийн төрөл"
              rules={[{ required: true, message: 'Төлбөрийн төрөл сонгоно уу' }]}
            >
              <Select>
                <Select.Option value="CONTRACT">Гэрээт</Select.Option>
                <Select.Option value="PRE_PAID">Урьдчилсан</Select.Option>
                <Select.Option value="POST_PAID">Дараа</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="phone1"
              label="Утас 1"
              rules={[{ required: true, message: 'Утас оруулна уу' }]}
            >
              <Input placeholder="Утас оруулна уу" />
            </Form.Item>
            <Form.Item name="phone2" label="Утас 2">
              <Input placeholder="Утас 2 оруулна уу" />
            </Form.Item>
            <Form.Item
              name="streetNo"
              label="Харилцагчын хаяг"
              rules={[{ required: true, message: 'Харилцагчын хаяг сонгоно уу' }]}
            >
              <Zipcode />
            </Form.Item>
            <Form.Item name="w3w" label="what3words">
              <Input placeholder="what3words оруулна уу" />
            </Form.Item>
            <Form.Item
              name="addressDetail"
              label="Дэлгэрэнгүй хаяг"
              rules={[{ required: true, message: 'Дэлгэрэнгүй хаяг оруулна уу' }]}
            >
              <Input.TextArea rows={4} placeholder="Дэлгэрэнгүй хаяг оруулна уу" />
            </Form.Item>
            <Form.Item name="send" label="SMS илгээх">
              <Select>
                <Select.Option value="NO">Үгүй</Select.Option>
                <Select.Option value="YES">Тийм</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="status"
              label="Төлөв"
              rules={[{ required: true, message: 'Төлөв сонгоно уу' }]}
            >
              <Select>
                <Select.Option value="ACTIVE">Идэвхтэй</Select.Option>
                <Select.Option value="INACTIVE">Идэвхгүй</Select.Option>
                <Select.Option value="DELETED">Устгасан</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Flex justify="end" gap={24}>
                <Link to={`/settings/customer`}>
                  <Button htmlType="button">Буцах</Button>
                </Link>
                <Button loading={loading} type="primary" htmlType="submit">
                  Хадгалах
                </Button>
              </Flex>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Card>
  );
};

export default CustomerUpdate;
